import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { PaginatedResponseInterface } from '@sport-activities/sdk';
import { Observable, of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { Advice } from '~/attendee/model/advice.model';
import { AdviceService } from '~/attendee/service/advice.service';
import { AdviceSectionCms } from '~/cms/interfaces/homepage.cms.interface';
import { SportsService } from '~/core/services/sports/sports.service';

interface LinkType {
  parameters?: { [key: string]: string };
  path: string;
  text: string;
}

@Component({
  selector: 'app-cms-advice-section',
  templateUrl: './advice-section.component.html',
})
export class CmsAdviceSectionComponent implements OnInit, OnDestroy {
  @Input() public section: AdviceSectionCms;

  private readonly subscriptions = new Subscription();
  public advices: Advice[];
  public seeMorelink: LinkType;

  constructor(
    private readonly adviceService: AdviceService,
    private readonly translateService: TranslateService,
    protected readonly localizeRouterService: LocalizeRouterService,
    private readonly sportsService: SportsService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.loadAdvices().subscribe((instances) => {
      this.advices = instances.items;
    });

    this.seeMorelink = this.getLink();
  }

  private loadAdvices(): Observable<PaginatedResponseInterface<Advice>> {
    const sportGroupArray = this.section.sport_group.split(',').map((x) => +x);

    let baseObs$ = this.sportsService.getSportsGroups({
      identifiers: sportGroupArray,
    });

    if (!sportGroupArray) {
      baseObs$ = of([]);
    }

    // get sport by sport groups
    return baseObs$.pipe(
      mergeMap((groups) => {
        // flatten to array of sports
        const sportsByGroup = groups.reduce(
          (accumulator, currentValue) =>
            accumulator.concat(currentValue?.sports),
          []
        );

        // retrieve advices
        return this.adviceService.getAdviceContents({
          dd_sports: sportsByGroup.join(','), // array values are expected with ,
          page: 1,
          items_per_page: this.section.number_to_display,
        });
      })
    );
  }

  public getLink(): LinkType {
    const route = '/attendee.result-page/attendee.advice';
    const ctaText = 'attendee.advice_collection.cta.see_more.label';

    return {
      text: this.translateService.instant(ctaText),
      path: this.localizeRouterService.translateRoute(route).toString(),
      parameters: { sportGroups: this.section.sport_group },
    };
  }
}
